import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Snackbar,
  Box,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SESSION_WARNING_WINDOW_MS, useAuth } from '../../context/AuthContext';
import { handleAuthError } from '../../utils/authErrorHandler';
import { getMsUntilExpiry } from '../../utils/tokenUtils';

import type { SessionExpiryModalProps } from './interfaces/sessionExpiryModalProps';
import { sessionExpiryModalStyles } from './styles/sessionExpiryModalStyles';

const COUNTDOWN_TICK_MS = 1_000;

const formatSeconds = (ms: number): string => {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;

  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};

export const SessionExpiryModal: React.FC<SessionExpiryModalProps> = ({
  totalWarningMs = SESSION_WARNING_WINDOW_MS,
}) => {
  const { sessionState, extendSession, logout } = useAuth();
  const open = sessionState === 'warning';

  const [remainingMs, setRemainingMs] = useState<number>(() => getMsUntilExpiry() ?? 0);
  const [extending, setExtending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const stayBtnRef = useRef<HTMLButtonElement | null>(null);

  // Countdown loop — only ticks while modal is open.
  useEffect(() => {
    if (!open) return;
    setRemainingMs(getMsUntilExpiry() ?? 0);
    const id = window.setInterval(() => {
      setRemainingMs(getMsUntilExpiry() ?? 0);
    }, COUNTDOWN_TICK_MS);

    return () => window.clearInterval(id);
  }, [open]);

  // Pull focus to the primary action when the dialog opens for keyboard users.
  useEffect(() => {
    if (open) {
      // requestAnimationFrame so MUI has rendered the button by the time we focus.
      const raf = requestAnimationFrame(() => stayBtnRef.current?.focus());

      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  const handleStay = useCallback(async () => {
    setExtending(true);
    try {
      const ok = await extendSession();

      if (ok) {
        setSnackbarOpen(true);
      } else {
        handleAuthError('session_expired');
      }
    } finally {
      setExtending(false);
    }
  }, [extendSession]);

  const handleSignOut = useCallback(async () => {
    await logout();
    window.location.replace('/login?reason=signed_out');
  }, [logout]);

  const progressValue = useMemo(() => {
    if (totalWarningMs <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, remainingMs / totalWarningMs));

    return ratio * 100;
  }, [remainingMs, totalWarningMs]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // ESC = explicit sign-out. Ignore backdrop clicks so users can't
          // accidentally dismiss the warning without deciding.
          if (reason === 'escapeKeyDown') {
            void handleSignOut();
          }
        }}
        aria-labelledby="session-expiry-title"
        aria-describedby="session-expiry-description"
      >
        <DialogTitle id="session-expiry-title">Your session is about to expire</DialogTitle>
        <DialogContent>
          <Box
            role="alertdialog"
            data-testid="session-expiry-modal"
            sx={sessionExpiryModalStyles.content}
          >
            <Typography id="session-expiry-description">
              For your security, you&apos;ll be signed out soon. Stay signed in to keep working.
            </Typography>
            <Box sx={sessionExpiryModalStyles.countdownRow}>
              <Typography variant="body2" color="text.secondary">
                Time remaining:
              </Typography>
              <Typography
                variant="body1"
                sx={sessionExpiryModalStyles.countdown}
                aria-live="polite"
                data-testid="session-countdown"
              >
                {formatSeconds(remainingMs)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={sessionExpiryModalStyles.progress}
              aria-label="Time remaining until session expires"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSignOut}
            color="inherit"
            data-testid="session-signout-btn"
            disabled={extending}
          >
            Sign out
          </Button>
          <Button
            ref={stayBtnRef}
            onClick={handleStay}
            variant="contained"
            color="primary"
            data-testid="session-stay-btn"
            disabled={extending}
          >
            {extending ? 'Refreshing…' : 'Stay signed in'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3_000}
        onClose={() => setSnackbarOpen(false)}
        message="Session extended"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default SessionExpiryModal;
